import React, { useState, useEffect, useContext, useRef, forwardRef } from 'react';
import { HeaderContext } from '../../../HeaderContext';
import { DataSpaceViewContext } from '../context/DataSpaceViewContext';
import { UserProfileContext } from '../../../UserProfileContext';
import DataTable from './DataTable';
import { Alert } from '@material-ui/lab';
import LuxonAdapter from '@material-ui/lab/AdapterLuxon';
import LocalizationProvider from '@material-ui/lab/LocalizationProvider';
import { DateTime } from 'luxon';
import DetailsPane from './DetailsPane';
import CSVExporter from './CSVExporter';
import {
	makeStyles,
	CircularProgress,
	Backdrop,
	Snackbar,
	Button,
	Grid,
	Dialog,
	DialogTitle,
	DialogContent,
	TextField,
	IconButton,
	InputAdornment,
	SvgIcon,
} from '@material-ui/core/';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddchartOutlinedIcon from '@mui/icons-material/AddchartOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import DisabledByDefaultIcon from '@mui/icons-material/DisabledByDefault';
import AssistantIcon from '@mui/icons-material/Assistant';
import SearchIcon from '@material-ui/icons/Search';
import { trackEvent } from '../../../utils/eventTracking';
import MyDataSpacesViewContext from '../context/MyDataSpacesViewContext';
import getLogo from '../../../utils/getLogo';
import {
	updateDataSpaceStateNew,
	getDataSpaces,
	getDataSpaceRows,
	updateDataSpaceData,
	getDataSpaceCSVNew,
	getSelectedDataSpaceViewNew,
	retrieveDataSpaceStateNew,
} from '../../../api_helper/api';
import fileDownload from 'js-file-download';
import './DataSpaceView.css';
import SnackbarAlertContext from '../../../context/SnackbarAlertContext';
import ShareDataSpaceDialog from '../ShareDataSpaceDialog';
import { ReactComponent as AddFormulaIcon } from '../../../assets/icons/add-formula.svg';
import RightSidebar from './RightSidebar';
import { getUserMessage } from '../../../utils/handleError';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import MenuIcon from '@material-ui/icons/Menu';
import DataSpaceViewMenu from './DataSpaceViewMenu';
import _ from 'lodash';

const useStyles = makeStyles((theme) => ({
	rightside_component_group: {
		display: 'inline-flex',
		justifyContent: 'end',
		alignItems: 'center',
		margin: '0 0.25rem 0.5rem 0',
	},
	individualButton: {
		'@media (max-width: 768px)': {
			display: 'none',
		},
	},
	menuButton: {
		display: 'none',
		'@media (max-width: 768px)': {
			display: 'inline-flex',
			height: 'fit-content',
			paddingTop: '0.4rem',
			paddingBottom: '0.4rem',
		},
	},
	button: {
		height: 'fit-content',
		paddingTop: '0.4rem',
		paddingBottom: '0.4rem',
	},
	backdrop: {
		zIndex: theme.zIndex.drawer + 1,
		color: '#fff',
	},
	datasetField: {
		width: '100%',
		height: 'fit-content',
		padding: '0.3rem',
		backgroundColor: '#C4C4C4',
		borderRadius: '0.2rem',
		transition: '0.3s',
		'&:hover': {
			backgroundColor: '#C4C4C4',
			filter: 'brightness(0.75)',
		},
		'& > span': {
			width: '100%',
			display: 'inline',
			color: '#000000',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
		},
	},
	datasetFieldSelected: {
		backgroundColor: '#0DBC42',
		'&:hover': {
			backgroundColor: '#0DBC42',
			filter: 'brightness(0.75)',
		},
	},
	seeDetailsButton: {
		color: 'black',
	},
	columnButtonGridItem: {
		flexBasis: '100%',
		maxWidth: '100%',
	},
	'@media (min-width: 450px)': {
		columnButtonGridItem: {
			flexBasis: '50%',
			maxWidth: '50%',
		},
	},
	'@media (min-width: 640px)': {
		columnButtonGridItem: {
			flexBasis: '33.3%',
			maxWidth: '33.3%',
		},
	},
}));

const dateRegexes = [
	{
		// "game_time": "Wed, 02 Mar 2019 22:00:00 GMT" Leading 0 on single digit days.
		dateType: 'httpStandard',
		regex: new RegExp('[A-z]{3}, [0-9]{2} [A-z]{3} [0-9]{4} [0-9]{2}:[0-9]{2}:[0-9]{2} GMT'),
	},
	{
		// "age": "May 5, 1991" (No leading 0 on single digit day)
		dateType: 'mon_dd,_yyyy',
		regex: new RegExp(
			'((?:January|February|March|April|May|June|July|August|September|October|November|December) [0-9]{1,2}, [0-9]{4})',
		),
	},
	{
		// "date_injured": "2019-9-27" (No leading 0 on single digit month/day)
		dateType: 'yyyy-mm-dd',
		regex: new RegExp('^[0-9]{4}-([0-9]{2}|[0-9]{1})-([0-9]{2}|[0-9]{1})$'),
	},
	{
		//"timestamp": "1568948695"
		dateType: 'unixTimestamp',
		regex: new RegExp('^[0-9]{8}([0-9]{1,2})?$'),
	},
];

// Converts all date values to predefined uniform formats and rounds numeric values
const applyCustomFormats = (dataSpaceData) => {
	let { data, columns } = dataSpaceData;
	const dateColumns = identifyDateColumnsAndType(dataSpaceData);
	const dateColumnFields = new Set(dateColumns.map((dc) => dc.field));

	// Identify numeric columns (fields that are not dates and have number type in data)
	// We scan the first row or check all rows if needed, but checking the value type during iteration is safer
	// However, to keep it efficient, we can check type inside the loop.

	data.forEach((row) => {
		// Handle Dates
		dateColumns.forEach(({ field, dateType }) => {
			if (field in row) {
				row[field] = convertDateToCustomISOFormat(dateType, row[field]);
			}
		});

		// Handle Number Rounding (moved from backend)
		for (let field in row) {
			if (typeof row[field] === 'number' && !dateColumnFields.has(field)) {
				row[field] = Math.round(row[field] * 1000) / 1000;
			}
		}
	});
	return data;
};

// Converts date or datetime to ISO value and adds suffix
// identifying Date type
const convertDateToCustomISOFormat = (dateType, actualDate) => {
	if ([null, undefined].includes(actualDate)) return actualDate;

	let luxonDate;
	let isoDate;
	switch (dateType) {
		case 'yyyy-mm-dd':
			luxonDate = DateTime.fromFormat(actualDate, 'yyyy-M-d');
			isoDate = luxonDate.toISO(luxonDate) + '_DATE';
			break;
		case 'httpStandard':
			luxonDate = DateTime.fromHTTP(actualDate);
			isoDate = luxonDate.toISO(luxonDate) + '_DATETIME';
			break;
		case 'mon_dd,_yyyy':
			luxonDate = DateTime.fromFormat(actualDate, 'LLLL d, yyyy');
			isoDate = luxonDate.toISO(luxonDate) + '_DATE';
			break;
		case 'unixTimestamp':
			luxonDate = DateTime.fromSeconds(parseInt(actualDate));
			isoDate = luxonDate.toISO(luxonDate) + '_DATETIME';
			break;
		default:
			throw new Error('Invalid date format specified: ' + dateType);
	}

	return isoDate;
};

// Identify columns containing Date values and their initial formats
const identifyDateColumnsAndType = (dataSpaceData) => {
	const { columns, data } = dataSpaceData;

	const fieldArray = columns.map((col) => col.field);
	const dateColumns = [];

	fieldArray.forEach((field) => {
		let dataType = '';
		let value;
		let i = 0;
		while (i < data.length && dataType === '') {
			if (typeof data[i][field] !== 'undefined') {
				dataType = typeof data[i][field];
				value = data[i][field];
			}
			i++;
		}

		i = 0;
		if (dataType === 'string') {
			// Check if it is a date and output console info to check method
			let isDateTypeFound = false;
			while (!isDateTypeFound && i < dateRegexes.length) {
				isDateTypeFound = dateRegexes[i].regex.test(value);
				if (isDateTypeFound) {
					dateColumns.push({
						field: field,
						dateType: dateRegexes[i].dateType,
					});
				}
				i++;
			}
		}
	});

	return dateColumns;
};

export default forwardRef(function DataSpaceViewNew(props, ref) {
	// Ref for RightSidebar imperative reset
	const rightSidebarRef = useRef(null);
	const classes = useStyles();
	// To open/close the right sidebar
	const [showModal, setShowModal] = useState(false);
	// To modify SportWise header
	const setHeaderContent = useContext(HeaderContext)[1];

	const dataTableRef = useRef(null);

	// To store and use view variables without prop drilling
	const dataSpaceViewContext = useContext(DataSpaceViewContext);
	const setDataSpaceId = dataSpaceViewContext.dataSpaceId[1];

	const myDataSpacesViewContext = useContext(MyDataSpacesViewContext);

	const snackbarAlertContext = useContext(SnackbarAlertContext);

	// Loading Backdrop
	const [loadingOpen, setLoadingOpen] = useState(true);

	// Menu for mobile view
	const [menuAnchorEl, setMenuAnchorEl] = useState(null);
	const handleMenuClick = (event) => setMenuAnchorEl(event.currentTarget);
	const handleMenuClose = () => setMenuAnchorEl(null);

	// CSV export state
	const [csvLoading, setCsvLoading] = useState(false);

	const handleExportCSV = async () => {
		handleMenuClose();
		setCsvLoading(true);
		try {
			const result = await getDataSpaceCSVNew(props.dataSpaceId);
			trackEvent({
				userDetails: { userId: user._id, email: user.email },
				eventDetails: {
					types: ['KissMetrics', 'Segment', 'Encharge', 'GA4', 'AppInsights'],
					eventName: 'WebApp_User Exports CSV From Their DataSpace',
				},
			});
			fileDownload(result.data, `${myDataSpacesViewContext.myDataSpacesView.selectedDataSpace.name}.csv`);
		} catch (err) {
			displayAlert(
				'error',
				<div>
					There was an error processing your CSV. Please try again later.
					<br />
					If the issue persists please report it with our&nbsp;
					<a
						href="https://rolling-wave.atlassian.net/servicedesk/customer/portal/1/group/1/create/1"
						rel="noreferrer"
						target="_blank"
					>
						service desk
					</a>
					.<br />
					<br />
					<b>
						<i>Error details: </i>
					</b>
					{err}
				</div>,
			);
		} finally {
			setCsvLoading(false);
		}
	};

	const [isSelectColumnsDialogVisible, setIsSelectColumnsDialogVisible] = useState(false);

	const [selectColumnsDialogSearchInput, setSelectColumnsDialogSearchInput] = useState('');

	// Details Pane
	const [detailsOpen, setDetailsOpen] = useState(false);
	const handleClickSeeDetails = () => setDetailsOpen(true);
	const onDetailsClose = () => setDetailsOpen(false);

	// Snackbar and alert
	const [snackOpen, setSnackOpen] = useState(false);
	const onSnackClose = () => setSnackOpen(false);
	const [snackMessage, setSnackMessage] = useState('');
	const [alertSeverity, setAlertSeverity] = useState('');
	const [snackAutoHideMs, setSnackAutoHideMs] = useState(5000);

	const user = useContext(UserProfileContext)[0];

	const displayAlert = (severity, message) => {
		setAlertSeverity(severity);
		setSnackMessage(message);
		setSnackOpen(true);
	};

	// Hold table data
	const [columns, setColumns] = useState([]);
	const [rows, setRows] = useState([]);
	const [dataSpaceDataReady, setDataSpaceDataReady] = useState(false);

	// Hold name of LMAPI (if any). Used when refreshing user's DataSpace
	const [lmapiName, setLmapiName] = useState();

	const [internalTableState, setInternalTableState] = useState(null);
	const [initialFilterStates, setInitialFilterStates] = useState(null);

	const [selectedColumns, setSelectedColumns] = useState([]); // Used in hide/show column toggle menu

	// Calculated Column Panel
	const [calcColPanelOpen, setCalcColPanelOpen] = useState(false);
	const handleCloseCalcColPanel = () => setCalcColPanelOpen(false);

	const [isDataSpaceExpanded, setIsDataSpaceExpanded] = useState(false);

	const selectColumnsDialogSearchChangeHandler = (e) => {
		setSelectColumnsDialogSearchInput(e.target.value);
	};

	const columnClickHandler = (clickedColumn) => {
		const isColumnBeingAdded = !selectedColumns.some(
			(selectedColumn) => selectedColumn.field === (clickedColumn.name || clickedColumn.field),
		);

		let updatedColumns = isColumnBeingAdded
			? [...selectedColumns, clickedColumn]
			: [...selectedColumns].filter(
					(selectedColumn) => selectedColumn.field !== (clickedColumn.name || clickedColumn.field),
			  );

		// Get corresponding column values from columns
		updatedColumns = updatedColumns.map((updatedColumn) => {
			return columns.find((column) => column.field === (updatedColumn.field || updatedColumn.name));
		});

		// Pass updatedColumns to existing column select handler.
		onColumnToggle({ value: updatedColumns });
	};

	const deselectAllColumnsClickHandler = () => {
		// Pass updatedColumns to existing column select handler.
		onColumnToggle({ value: [columns[0]] });
	};

	const selectAllColumnsClickHandler = () => {
		let updatedColumns = [...new Set([...selectedColumns, ...columns])];

		// Get corresponding column values from columns
		updatedColumns = updatedColumns.map((updatedColumn) => {
			return columns.find((column) => column.field === (updatedColumn.field || updatedColumn.name));
		});

		// Pass updatedColumns to existing column select handler.
		onColumnToggle({ value: updatedColumns });
	};

	const selectColumnsDatasetColumns = (dataset) => {
		let columnButtons;
		if (dataset === 'Calculated') {
			columnButtons = columns?.reduce((accumulator, column) => {
				// If column doesn't match search filter, isn't a Calculated Column, or is excluded don't show column.
				if (
					(selectColumnsDialogSearchInput &&
						!(column.header || column.field).toLowerCase().includes(selectColumnsDialogSearchInput.toLowerCase())) ||
					column.dataSourceName !== 'Calculated' ||
					column.isExcluded
				) {
					return accumulator;
				}

				return [
					...accumulator,
					<Grid key={column.name} item xs={6} md={4} className={classes.columnButtonGridItem}>
						<Button
							className={
								classes.datasetField +
								(selectedColumns.some((selectedColumn) => selectedColumn.field === column.field)
									? ' ' + classes.datasetFieldSelected
									: '')
							}
							onClick={() => {
								columnClickHandler(column);
							}}
						>
							{column.header || column.field}
						</Button>
					</Grid>,
				];
			}, []);
		} else {
			columnButtons = dataset.fields.reduce((accumulator, column) => {
				// If column doesn't match search filter or is excluded don't show column.
				if (
					(selectColumnsDialogSearchInput &&
						!(column.displayName || column.name)
							.toLowerCase()
							.includes(selectColumnsDialogSearchInput.toLowerCase())) ||
					column.isExcluded
				) {
					return accumulator;
				}

				return [
					...accumulator,
					<Grid key={column.name} item xs={6} md={4} className={classes.columnButtonGridItem}>
						<Button
							className={
								classes.datasetField +
								(selectedColumns.some((selectedColumn) => selectedColumn.field === column.name)
									? ' ' + classes.datasetFieldSelected
									: '')
							}
							onClick={() => {
								columnClickHandler(column);
							}}
						>
							{column.displayName || column.name}
						</Button>
					</Grid>,
				];
			}, []);
		}

		return columnButtons?.length ? (
			columnButtons
		) : (
			<Grid item xs={12}>
				No columns found
			</Grid>
		);
	};

	// Used in hide/show MultiSelect component. Modifies selectedColumns and state based
	// on column toggling
	const onColumnToggle = async (e) => {
		trackEvent({
			userDetails: { userId: user._id, email: user.email },
			eventDetails: {
				types: ['KissMetrics', 'Segment', 'AppInsights', 'GA4'],
				eventName: 'WebApp_User shows/hides DataSpace column',
			},
		});

		// Table crashes if no columns selected. If user selects 0 columns, the first column will automatically be selected.
		setSelectedColumns(e.value.length ? e.value : [columns[0]]);
	};

	// Refresh user's LMAPI data, reload DataSpace
	const [lastRefreshDateTime, setLastRefreshDateTime] = useState('');
	const refreshDataSpace = async () => {
		// Reset and delete agent/assistant in RightSidebar before refresh
		if (rightSidebarRef.current && rightSidebarRef.current.resetAndDeleteAssistant) {
			console.log('Resetting and deleting assistant in RightSidebar before refresh');
			await rightSidebarRef.current.resetAndDeleteAssistant();
		}
		// Reliant will not be refreshed here as it should be on a schedule or some other system.

		setLoadingOpen(true); // Initiates loading backdrop

		try {
			let dataSpaceData = (await updateDataSpaceData(props.dataSpaceId)).data;

			let rows = applyCustomFormats(dataSpaceData);
			const columns = generateAndAppendColumnToggleLabels(dataSpaceData.columns);
			setRows(rows);
			setColumns(columns);

			setLastRefreshDateTime(new Date().toLocaleString());

			trackEvent({
				userDetails: { userId: user._id, email: user.email },
				eventDetails: {
					types: ['KissMetrics', 'AppInsights', 'Segment', 'Encharge', 'GA4'],
					eventName: 'User refreshed DataSpace.',
				},
			});
			setAlertSeverity('success');
			setSnackMessage('Your DataSpace has been refreshed.');
			setSnackAutoHideMs(5000);
		} catch (e) {
			// If something goes wrong alert the user and have them retry
			trackEvent({
				userDetails: { userId: user._id, email: user.email },
				eventDetails: {
					types: ['Segment', 'Encharge', 'GA4'],
					eventName: 'User encountered issue when refreshing DataSpace.',
				},
			});
			setLoadingOpen(false);
			snackbarAlertContext.setSnackbarAlert({ msg: getUserMessage(e), severity: 'error' });
			return;
		}

		// Close loading screen, show snackbar alert
		setLoadingOpen(false);
		setSnackOpen(true);

		// Initialize agent after refresh completes
		if (rightSidebarRef.current && rightSidebarRef.current.initializeAgent) {
			console.log('Initializing agent after DataSpace refresh');
			await rightSidebarRef.current.initializeAgent();
		}
	};

	// Generate labels for column dropdown toggle
	const generateAndAppendColumnToggleLabels = (columns) => {
		return columns.map((c) => {
			return {
				...c,
				columnToggleLabel: c.header + ' - ' + c.dataSourceName,
			};
		});
	};

	// This function updates the column header in local and database states when the user renames
	// a column in the DataSpace. It is used as a callback in the ColumnHeader component.
	const renameColumnHeader = (field, newHeader) => {
		// Validation
		const errors = validateNewHeaderName(newHeader);
		if (errors) {
			displayAlert('error', errors);
			return;
		}

		// Rename column header locally
		let columnsCopy = [...columns];
		columnsCopy.find((x) => x.field === field).header = newHeader;
		columnsCopy = generateAndAppendColumnToggleLabels(columnsCopy);
		setColumns(columnsCopy);

		// Rename column header in DB
		const payload = {
			dataSpaceId: props.dataSpaceId,
			mode: 'headerNamesState',
			headerNamesState: {
				field: field,
				header: newHeader,
			},
		};
		updateDataSpaceStateNew(payload);
	};

	// Store dataSpaceId in context so it can be used later without prop drilling
	useEffect(() => {
		setDataSpaceId(props.dataSpaceId);
	}, []);

	// When renaming a column, validates the new header name. Returns an unordered list
	// of errors for display in alert. (eg <ul><li>{error message}</li></ul>)
	function validateNewHeaderName(newHeader) {
		const errors = [];
		let alertContent;
		// Check header name is unique
		if (columns.find((c) => c.header.toLowerCase() === newHeader.toLowerCase())) {
			errors.push(`There is already a column with the name "${newHeader}". Please choose a new column name.`);
			// displayAlert('error', `There is already a column with the name "${newHeader}". Please choose a new column name.`);
		}
		// Check new header is not empty string
		if (!/\S/.test(newHeader) || newHeader === '') {
			errors.push('Please enter a name that is not whitespace.');
		}
		// Create unordered list of errors
		if (errors.length > 0) {
			alertContent = (
				<ul>
					{errors.map((a) => (
						<li>{a}</li>
					))}
				</ul>
			);
		}
		return alertContent;
	}

	// Get lmapiName for DataSpace refresh, set last refresh date, build and set header, populate DataSpace
	useEffect(() => {
		const getDataSpaceData = async () => {
			setDataSpaceDataReady(false);
			try {
				// Get DataSpace Rows, Internal State, and Toggled Columns State
				const [getDataSpacesRes, getDataSpaceRowsRes, internalStateRes, toggledColumnsStateRes] = await Promise.all([
					getDataSpaces(props.dataSpaceId),
					getDataSpaceRows(props.dataSpaceId),
					retrieveDataSpaceStateNew(props.dataSpaceId, 'internalState'),
					retrieveDataSpaceStateNew(props.dataSpaceId, 'toggledColumnsState'),
				]);

				const dataSpace = getDataSpacesRes.data.dataSpace;
				dataSpaceViewContext.setDataSpace({ ...dataSpace });
				myDataSpacesViewContext.setSelectedDataSpace({ ...dataSpace });

				if (dataSpace.lastRefreshedDate) {
					setLastRefreshDateTime(new Date(dataSpace.lastRefreshedDate).toLocaleString());
				} else {
					setLastRefreshDateTime(new Date().toLocaleString());
				}

				// Set LMAPI name (Used when DataSpace is refreshed)
				if (lmapiName) setLmapiName(lmapiName);

				// Build string to display in header below DataSpace Name
				let headerSubtitle;
				let emoji;
				let selectedSport = dataSpace.datasets[0].tags.sport;
				let selectedLeague = undefined;

				// Get emoji
				switch (selectedSport) {
					case 'NFL':
						emoji = '🏈';
						break;
					case 'NHL':
						emoji = '🏒';
						break;
					case 'MLB':
						emoji = '⚾';
						break;
					case 'NBA':
						emoji = '🏀';
						break;
					default:
						emoji = '😀';
				}

				// Build header subtitle
				const fantasyLeagueDataset = dataSpace.datasets.find((dataset) =>
					['Yahoo! Fantasy Sports', 'Fantrax'].includes(dataset.tags.api),
				);

				if (fantasyLeagueDataset) {
					headerSubtitle = `${selectedSport}, ${fantasyLeagueDataset.leagueName} ${emoji}`;
				} else headerSubtitle = `${selectedSport} ${emoji}`;

				// Set header
				setHeaderContent(null);

				setJoinedDataSpaceData({
					columns: dataSpace.columnMetadata,
					data: getDataSpaceRowsRes.data.rows,
					headerNamesState: dataSpace.headerNamesState,
				});

				// Determine display view
				let initialView = dataSpace.views.find((view) => view.isDisplayView);
				if (!initialView) {
					initialView = dataSpace.views.find((view) => view.name === 'Default');
				}

				let viewDetails = null;
				if (initialView) {
					// Fetch view details
					const res = await getSelectedDataSpaceViewNew({
						dataSpaceId: props.dataSpaceId,
						viewId: initialView.id,
					});
					viewDetails = res.view;

					// Convert Dates if needed
					if (viewDetails.externalProperties && viewDetails.externalProperties.filterStates) {
						viewDetails.externalProperties.filterStates = convertISOStringsToDateTimes(
							viewDetails.externalProperties.filterStates,
						);
					}

					// Set selected view in context
					dataSpaceViewContext.setSelectedView(viewDetails);
				}

				// Process toggledColumns and View Settings
				if (viewDetails) {
					// Apply view columns
					if (viewDetails.externalProperties && viewDetails.externalProperties.selectedColumns) {
						setSelectedColumns(viewDetails.externalProperties.selectedColumns);
					}
					// Set initial filter states
					if (viewDetails.externalProperties && viewDetails.externalProperties.filterStates) {
						setInitialFilterStates(viewDetails.externalProperties.filterStates);
					}
				} else {
					// Fallback to toggledColumnsState logic
					if (
						toggledColumnsStateRes.toggledColumnsState &&
						toggledColumnsStateRes.toggledColumnsState.length > 0
					) {
						let toggleState = toggledColumnsStateRes.toggledColumnsState
							.map((toggledCol) => {
								const column = dataSpace.columnMetadata.find((c) => c.field === toggledCol);
								return column;
							})
							.filter((x) => x);
						setSelectedColumns(toggleState);
					} else {
						setSelectedColumns(dataSpace.columnMetadata);
					}
				}

				// Process internalState
				if (viewDetails && viewDetails.internalProperties && viewDetails.internalProperties.state) {
					let iState = { ...viewDetails.internalProperties.state };
					iState.first = 0; // Reset pagination
					setInternalTableState(iState);
				} else {
					let iState = internalStateRes.internalState ? { ...internalStateRes.internalState } : {};
					if (!iState.rows || Object.keys(iState).length === 0) {
						iState.rows = 50;
						iState.first = 0;
					}
					setInternalTableState(iState);
				}
			} catch (e) {
				myDataSpacesViewContext.changeViewHandler('home');
				snackbarAlertContext.setSnackbarAlert({
					msg: 'Failed to load DataSpace. Please try again later. If the issue persists, please contact support.',
					severity: 'error',
					autoHide: 5000,
					isSnackbarOpen: true,
				});
			} finally {
				setLoadingOpen(false);
				setDataSpaceDataReady(true);
			}
		};

		getDataSpaceData();

		// Clear header when exiting view
		return function clearHeader() {
			setHeaderContent(<p>SportWise</p>);
		};
	}, [myDataSpacesViewContext.selectedDataSpace?.isPublic]);

	function setJoinedDataSpaceData(dataSpaceData) {
		let { columns, headerNamesState } = dataSpaceData;
		const data = applyCustomFormats(dataSpaceData);

		// Populate table
		setRows(data);

		columns = columns.map((column) => {
			const headerName = headerNamesState?.find((headerName) => headerName.field === column.field);
			if (headerName) column.header = headerName.header;
			return column;
		});
		columns = generateAndAppendColumnToggleLabels(columns);
		setColumns(columns);
	}

	// This function is used to append new Calculated Column data (row data and column details) to the existing row and column state.
	const appendCalculatedColumn = async (columnDetails) => {
		// Process and append column
		let col = [];
		col.push(columnDetails.calcColDetails);
		col = generateAndAppendColumnToggleLabels(col);
		setColumns((prev) => {
			const toSet = prev.concat(col);
			return toSet;
		});

		// Append (join) calculated row data
		const { calculatedRows } = columnDetails;
		setRows((rows) => {
			const newRows = [...rows];
			for (const i in newRows) {
				if (Object.keys(calculatedRows[i]).length > 0) {
					newRows[i] = {
						...newRows[i],
						...calculatedRows[i],
					};
				}
			}
			return newRows;
		});

		const dataSpace = (await getDataSpaces(props.dataSpaceId)).data.dataSpace;

		// Update column metadata with new calculated column
		dataSpaceViewContext.setDataSpace((prevState) => ({
			...prevState,
			columnMetadata: dataSpace.columnMetadata,
		}));

		// Filter Initialization for new calculated column occurs in DataTable
	};

	// This function is used to replace the values of a custom column after it has been edited and recalculated
	const applyModifiedCustomColumn = async (columnDetails) => {
		// If column is dependency of other column, use retrieveJoinedDataSpaceData
		// so dependent columns are recalculated as well
		if (columnDetails.calcColDetails.isDependency) {
			try {
				const dataSpace = (await getDataSpaces(props.dataSpaceId)).data.dataSpace;

				const columns = dataSpace.columnMetadata.map((column) => ({
					_id: column._id,
					field: column.field,
					header: column.header,
					dataSourceName: column.dataSourceName,
					description: column.description,
				}));

				const rows = (await getDataSpaceRows(props.dataSpaceId)).data.rows;

				setJoinedDataSpaceData({
					columns,
					data: rows,
					headerNamesState: dataSpaceViewContext.dataSpace.headerNamesState,
				});
			} catch (e) {
				props.handleDataSpaceLoadingError();
			}
		}
		// If no dependent columns, replace existing column values with new values
		else {
			const { calculatedRows } = columnDetails;
			const { field } = columnDetails.calcColDetails;
			setRows((rows) => {
				const newRows = [...rows];
				for (const i in newRows) {
					if (calculatedRows[i][field] || calculatedRows[i][field] === 0)
						newRows[i] = { ...newRows[i], [field]: calculatedRows[i][field] };
					else {
						const { [field]: deleted, ...rest } = newRows[i];
						newRows[i] = rest;
					}
				}
				return newRows;
			});
		}
	};

	// Deletes a custom column from the frontend. Works by re-retrieving and reloading
	// DataSpace data. This method avoids clashing with filter system (rather than removing
	// the column data  explicitly from the rows and columns).
	const deleteCustomColumn = async () => {
		try {
			const dataSpace = (await getDataSpaces(props.dataSpaceId)).data.dataSpace;

			const columns = dataSpace.columnMetadata.map((column) => ({
				_id: column._id,
				field: column.field,
				header: column.header,
				dataSourceName: column.dataSourceName,
				description: column.description,
			}));

			const rows = (await getDataSpaceRows(props.dataSpaceId)).data.rows;

			setJoinedDataSpaceData({
				columns,
				data: rows,
				headerNamesState: dataSpaceViewContext.dataSpace.headerNamesState,
			});
		} catch (e) {
			// // Display error, send user back to My DataSpaces
			props.handleDataSpaceLoadingError();
		}
	};

	const openCalculatedColumnPanelOnClick = () => {
		setCalcColPanelOpen(true);
		trackEvent({
			userDetails: { userId: user._id, email: user.email },
			eventDetails: {
				types: ['KissMetrics', 'AppInsights', 'Segment', 'Encharge', 'GA4'],
				eventName: 'User opened Calculated Column Panel.',
			},
		});
	};

	const chatbotButtonClickHandler = () => {
		setShowModal(!showModal);
		trackEvent({
			userDetails: { userId: user._id, email: user.email },
			eventDetails: {
				types: ['KissMetrics', 'Segment', 'Encharge', 'AppInsights'],
				eventName: 'WebApp_User launched chatbot.',
			},
		});
	};

	const expandDataSpaceClickHandler = () => {
		setIsDataSpaceExpanded((state) => !state);
	};

	// Convert ISO date strings back to DateTime objects for filter compatibility
	const convertISOStringsToDateTimes = (filterStates) => {
		for (const [key, value] of Object.entries(filterStates)) {
			if (value.startDate) {
				if (value.startDate !== null) filterStates[key].startDate = DateTime.fromISO(value.startDate);
				if (value.endDate !== null) filterStates[key].endDate = DateTime.fromISO(value.endDate);
			}
		}
		return filterStates;
	};

	// Handler to switch between views
	const handleSwitchView = async (viewId) => {
		try {
			const res = await getSelectedDataSpaceViewNew({ dataSpaceId: props.dataSpaceId, viewId });
			// Check if externalProperties and filterStates exist before accessing
			if (res.view.externalProperties && res.view.externalProperties.filterStates) {
				res.view.externalProperties.filterStates = convertISOStringsToDateTimes(
					res.view.externalProperties.filterStates,
				);
			}
			dataTableRef.current.loadView(res.view);
			dataSpaceViewContext.setSelectedView(res.view);
			handleMenuClose();
		} catch (e) {
			console.error('Failed to load view:', e);
			displayAlert('error', 'Failed to load view. Please try again.');
		}
	};

	return (
		<div className="dataspace-view">
			<RightSidebar
				ref={rightSidebarRef}
				showModal={showModal}
				setShowModal={setShowModal}
				dataspaceid={props.dataSpaceId}
				jwt={localStorage.getItem('jwt')}
				dataSpaceDataReady={dataSpaceDataReady}
			/>
			<LocalizationProvider dateAdapter={LuxonAdapter}>
			{internalTableState && (
				<DataTable
					ref={dataTableRef}
					internalTableState={internalTableState}
					setLoadingOpen={setLoadingOpen}
					initialFilterStates={initialFilterStates}
					columns={columns}
					rows={rows}
					dataSpaceId={props.dataSpaceId}
					renameHeader={renameColumnHeader}
					handleClickSeeDetails={handleClickSeeDetails}
					appendCalculatedColumn={appendCalculatedColumn}
					applyModifiedCustomColumn={applyModifiedCustomColumn}
					deleteCustomColumn={deleteCustomColumn}
					calcColPanelOpen={calcColPanelOpen}
					handleCloseCalcColPanel={handleCloseCalcColPanel}
					displayAlert={displayAlert}
					selectedColumns={selectedColumns}
					setSelectedColumns={setSelectedColumns}
					isDataSpaceExpanded={isDataSpaceExpanded}
					isEditingDisabled={props.isEditingDisabled}
					callToActionButton={props.callToActionButton}
				>
					<div className={classes.rightside_component_group}>
						{!props.isEditingDisabled  && (
								<IconButton
									className={classes.button}
									color="secondary"
									onClick={chatbotButtonClickHandler}
									style={{ width: '2.5rem', height: '2.5rem' }}
								>
									<AssistantIcon />
								</IconButton>
							)}

						{!props.isEditingDisabled && (
							<IconButton
								className={classes.button}
								color="secondary"
								onClick={refreshDataSpace}
								style={{ width: '2.5rem', height: '2.5rem' }}
							>
								<AutorenewOutlinedIcon />
							</IconButton>
						)}

						<IconButton
							className={classes.button}
							onClick={() => {
								setIsSelectColumnsDialogVisible(true);
							}}
							style={{ width: '2.5rem', height: '2.5rem' }}
						>
							<AddchartOutlinedIcon />
						</IconButton>

						{!props.isEditingDisabled && (
							<IconButton
								className={`${classes.button} ${classes.individualButton}`}
								onClick={openCalculatedColumnPanelOnClick}
								style={{ width: '2.5rem', height: '2.5rem' }}
							>
								<SvgIcon style={{ width: '1.3rem', height: '1.3rem' }}>
									<AddFormulaIcon />
								</SvgIcon>
							</IconButton>
						)}

						{!props.isEditingDisabled && (
							<div className={classes.individualButton}>
								<CSVExporter
									dataSpaceId={props.dataSpaceId}
									dataSpaceName={myDataSpacesViewContext.myDataSpacesView.selectedDataSpace.name}
									displayAlert={displayAlert}
								/>
							</div>
						)}

						<IconButton
							className={`${classes.button} ${classes.individualButton}`}
							style={{ width: '2.5rem', height: '2.5rem' }}
							onClick={handleClickSeeDetails}
						>
							<InfoOutlinedIcon />
						</IconButton>

						<Button
							className={classes.button}
							onClick={expandDataSpaceClickHandler}
							style={{ minWidth: '0', maxWidth: '2.5rem', height: '85%', padding: '0' }}
						>
							{isDataSpaceExpanded ? (
								<FullscreenExitIcon style={{ fontSize: 30 }} />
							) : (
								<FullscreenIcon style={{ fontSize: 30 }} />
							)}
						</Button>

						{/* Menu button for smaller screens */}
						<IconButton
							className={classes.menuButton}
							color="secondary"
							onClick={handleMenuClick}
							style={{ width: '2.5rem', height: '2.5rem' }}
						>
							<MenuIcon />
						</IconButton>

						<DataSpaceViewMenu
							anchorEl={menuAnchorEl}
							onClose={handleMenuClose}
							csvLoading={csvLoading}
							handleExportCSV={handleExportCSV}
							refreshDataSpace={refreshDataSpace}
							openCalculatedColumnPanelOnClick={openCalculatedColumnPanelOnClick}
							handleClickSeeDetails={handleClickSeeDetails}
							dataTableRef={dataTableRef}
							views={dataSpaceViewContext.dataSpace?.views || []}
							handleSwitchView={handleSwitchView}
							isEditingDisabled={props.isEditingDisabled}
						/>
					</div>
				</DataTable>
			)}
			</LocalizationProvider>

			<Dialog
				open={isSelectColumnsDialogVisible}
				onClose={() => {
					setIsSelectColumnsDialogVisible(false);
				}}
				fullWidth
			>
				<DialogTitle>
					Select Columns
					<IconButton
						aria-label="close"
						onClick={() => {
							setIsSelectColumnsDialogVisible(false);
						}}
						sx={{
							position: 'absolute',
							right: 8,
							top: 8,
							color: (theme) => theme.palette.grey[500],
						}}
					>
						<DisabledByDefaultIcon size="medium" />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					<TextField
						onChange={selectColumnsDialogSearchChangeHandler}
						style={{ width: '100%', marginBottom: '1rem' }}
						value={selectColumnsDialogSearchInput}
						placeholder="Search columns"
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							),
						}}
						variant="standard"
					/>
					{dataSpaceViewContext.dataSpace.datasets &&
						dataSpaceViewContext.dataSpace.datasets.map((dataset) => (
							<div key={dataset._id} style={{ width: '100%', marginBottom: '2rem' }}>
								<div style={{ height: '5rem', width: '100%', marginBottom: '1rem', display: 'flex' }}>
									<img
										src={getLogo({ api: dataset.tags.api, sport: dataset.tags.sport })}
										alt="logo"
										style={{ height: '100%', borderRadius: '0.3rem', marginRight: '0.5rem' }}
									/>
									<div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
										<h5 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>
											{(dataset.displayName || dataset.name) +
												(dataset.tags.timeFrame ? ` (${dataset.tags.timeFrame})` : '') +
												` (${dataset._id.substr(-3)})`}
										</h5>
										<p style={{ height: '100%', margin: '0', overflow: 'auto' }}>
											Select columns below to view them in your DataSpace
										</p>
									</div>
								</div>
								<Grid container spacing={0.5}>
									{selectColumnsDatasetColumns(dataset)}
								</Grid>
							</div>
						))}

					{/* Calculated Columns*/}
					<div style={{ width: '100%', marginBottom: '2rem' }}>
						<div style={{ height: '5rem', width: '100%', marginBottom: '1rem', display: 'flex' }}>
							<img
								src={getLogo({ api: null, sport: null })}
								alt="logo"
								style={{ height: '100%', borderRadius: '0.3rem', marginRight: '0.5rem' }}
							/>
							<div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
								<h5 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>Calculated Columns</h5>
								<p style={{ height: '100%', margin: '0', overflow: 'auto' }}>
									Select columns below to view them in your DataSpace
								</p>
							</div>
						</div>
						<Grid container spacing={0.5}>
							{selectColumnsDatasetColumns('Calculated')}
						</Grid>
					</div>
					{selectedColumns.length === columns.length ? (
						<Button variant="outlined" onClick={deselectAllColumnsClickHandler}>
							Deselect All Columns
						</Button>
					) : (
						<Button variant="outlined" onClick={selectAllColumnsClickHandler}>
							Select All Columns
						</Button>
					)}
				</DialogContent>
			</Dialog>

			<Backdrop className={classes.backdrop} open={loadingOpen}>
				<CircularProgress color="inherit" />
			</Backdrop>

			<ShareDataSpaceDialog />

			<Snackbar
				open={snackOpen}
				anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
				onClose={onSnackClose}
				autoHideDuration={snackAutoHideMs}
			>
				<Alert severity={alertSeverity}>{snackMessage}</Alert>
			</Snackbar>

			<DetailsPane
				open={detailsOpen}
				onClose={onDetailsClose}
				dataSpaceId={props.dataSpaceId}
				lastRefreshDateTime={lastRefreshDateTime}
				setLoadingOpen={setLoadingOpen}
				isEditingDisabled={props.isEditingDisabled}
			/>
		</div>
	);
});
